import { createTheme } from '@mui/material/styles';
import { getColors, fontFamily, monoFontFamily, radius, elevation } from './tokens';

/**
 * MUI theme built on Sprout tokens. Component overrides follow
 * documentcollectorfe/src/theme/muiTheme.js so both apps share one look.
 */
export function createAppTheme(mode = 'light') {
  const c = getColors(mode);

  return createTheme({
    palette: {
      mode,
      primary: {
        main: c.primary,
        dark: c.primaryHovered,
        light: c.primaryContainer,
        contrastText: c.onPrimary,
      },
      secondary: {
        main: c.primaryContainer,
        contrastText: c.onPrimaryContainer,
      },
      success: { main: c.success, contrastText: '#FFFFFF' },
      warning: { main: c.warning, contrastText: '#FFFFFF' },
      error: { main: c.danger, contrastText: '#FFFFFF' },
      info: { main: c.info, contrastText: '#FFFFFF' },
      background: { default: c.background, paper: c.surface },
      text: { primary: c.onSurface, secondary: c.supportingText, disabled: c.disabled },
      divider: c.outline,
      action: { hover: c.surfaceHovered, selected: c.primaryContainer },
      sprout: c,
    },
    typography: {
      fontFamily,
      h1: { fontSize: '2rem', fontWeight: 700, lineHeight: '2.5rem', letterSpacing: '-0.01em' },
      h2: { fontSize: '1.75rem', fontWeight: 700, lineHeight: '2.25rem', letterSpacing: '-0.01em' },
      h3: { fontSize: '1.5rem', fontWeight: 700, lineHeight: '2rem' },
      h4: { fontSize: '1.5rem', fontWeight: 700, lineHeight: '2rem' },
      h5: { fontSize: '1.25rem', fontWeight: 700, lineHeight: '1.75rem' },
      h6: { fontSize: '1rem', fontWeight: 600, lineHeight: '1.5rem' },
      subtitle1: { fontSize: '1rem', fontWeight: 600, lineHeight: '1.5rem' },
      subtitle2: { fontSize: '0.875rem', fontWeight: 600, lineHeight: '1.25rem' },
      body1: { fontSize: '0.875rem', fontWeight: 400, lineHeight: '1.25rem' },
      body2: { fontSize: '0.75rem', fontWeight: 400, lineHeight: '1rem' },
      caption: { fontSize: '0.75rem', fontWeight: 600, lineHeight: '1rem' },
      overline: { fontSize: '0.6875rem', fontWeight: 700, lineHeight: '1rem', letterSpacing: '0.08em' },
      button: { fontWeight: 700, textTransform: 'none' },
    },
    shape: { borderRadius: radius.large },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: c.background },
          code: { fontFamily: monoFontFamily },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: elevation.small,
            border: `1px solid ${c.outline}`,
            borderRadius: radius.large,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: radius.medium, fontWeight: 700, textTransform: 'none' },
          containedPrimary: {
            '&:hover': { backgroundColor: c.primaryHovered },
            '&:active': { backgroundColor: c.primaryPressed },
          },
          outlinedPrimary: {
            borderColor: c.primary,
            '&:hover': { backgroundColor: c.primaryContainer },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: { root: { borderRadius: radius.medium } },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: radius.circle, fontSize: '0.75rem', fontWeight: 600 },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 40,
            backgroundColor: c.surface,
            border: `1px solid ${c.outline}`,
            borderRadius: radius.large,
            padding: 2,
          },
          indicator: { display: 'none' },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 36,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            borderRadius: 6,
            margin: 2,
            color: c.onSurface,
            '&.Mui-selected': { backgroundColor: c.primary, color: c.onPrimary },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: radius.large, fontSize: '0.875rem', alignItems: 'flex-start' },
          standardInfo: { backgroundColor: c.infoContainer, color: c.onSurface },
          standardSuccess: { backgroundColor: c.successContainer, color: c.onSurface },
          standardWarning: { backgroundColor: c.warningContainer, color: c.onSurface },
          standardError: { backgroundColor: c.dangerContainer, color: c.onSurface },
        },
      },
      MuiAlertTitle: {
        styleOverrides: { root: { fontWeight: 700, fontSize: '0.875rem' } },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: c.outlineSubtle, fontSize: '0.875rem', padding: '12px 16px' },
          head: {
            fontWeight: 700,
            fontSize: '0.75rem',
            color: c.supportingText,
            backgroundColor: c.background,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          },
        },
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: c.outlineSubtle } },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: '#1A1A1A',
            color: '#FFFFFF',
            fontSize: '0.75rem',
            fontWeight: 600,
            borderRadius: radius.medium,
            padding: '4px 8px',
          },
          arrow: { color: '#1A1A1A' },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.medium,
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: c.primary },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: c.primary },
          },
          notchedOutline: { borderColor: c.outline },
        },
      },
      MuiListItemButton: {
        styleOverrides: { root: { borderRadius: radius.large } },
      },
      MuiDrawer: {
        styleOverrides: { paper: { backgroundColor: c.surface, borderRight: `1px solid ${c.outline}` } },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'inherit' },
        styleOverrides: {
          root: { backgroundColor: c.surface, borderBottom: `1px solid ${c.outline}`, color: c.onSurface },
        },
      },
    },
  });
}

export const muiTheme = createAppTheme('light');
